import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';
import SuggestionPictogramManager from './SuggestionPictogramManager';
import i18n from '../i18n';

const PromptEditor = () => {
  const { t } = useTranslation('promptEditor');
  const [prompts, setPrompts] = useState([]);
  const [imageThemes, setImageThemes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState({
    id: null,
    imageTheme: {},
    action: {},
    prompt: '',
    suggestions: []
  });
  const [availableSuggestions, setAvailableSuggestions] = useState([]);
  const [isNewTheme, setIsNewTheme] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedThemes, setExpandedThemes] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  
  const languages = Object.keys(i18n.services.resourceStore.data || { en: {} });

  // Fetch all prompts and suggestion pictograms
  const fetchPromptsAndSuggestions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [promptsResponse, suggestionsResponse] = await Promise.all([
        api.get('/api/admin/prompts'),
        api.get('/api/content/suggestion-pictograms')
      ]);
      
      setPrompts(promptsResponse.data.prompts);
      setAvailableSuggestions(suggestionsResponse.data.suggestions || []);
      
      // Extract unique image themes
      const themesSet = new Set();
      promptsResponse.data.prompts.forEach(prompt => {
        const themeKeys = Object.keys(prompt.imageTheme || {});
        if (themeKeys.length > 0) {
          themesSet.add(JSON.stringify(prompt.imageTheme));
        }
      });
      
      const themes = Array.from(themesSet).map(t => JSON.parse(t));
      setImageThemes(themes);
      
      // Initialize expanded state for each theme
      const initialExpandedState = {};
      themes.forEach(theme => {
        const key = getLocalizedValue(theme, currentLanguage);
        initialExpandedState[key] = false;
      });
      setExpandedThemes(initialExpandedState);
      
      setIsLoading(false);
    } catch (err) {
      setError(t('error.load'));
      setIsLoading(false);
      console.error('Error fetching data:', err);
    }
  };

  // Create new prompt
  const createPrompt = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await api.post('/api/admin/prompts', currentPrompt);
      await fetchPromptsAndSuggestions();
      resetForm();
    } catch (err) {
      setError(t('error.create'));
      setIsLoading(false);
      console.error('Error creating prompt:', err);
    }
  };

  // Update existing prompt
  const updatePrompt = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await api.put(`/api/admin/prompts/${currentPrompt.id}`, currentPrompt);
      await fetchPromptsAndSuggestions();
      resetForm();
    } catch (err) {
      setError(t('error.update'));
      setIsLoading(false);
      console.error('Error updating prompt:', err);
    }
  };

  // Delete a prompt
  const deletePrompt = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        setIsLoading(true);
        setError(null);
        await api.delete(`/api/admin/prompts/${id}`);
        await fetchPromptsAndSuggestions();
      } catch (err) {
        setError(t('error.delete'));
        setIsLoading(false);
        console.error('Error deleting prompt:', err);
      }
    }
  };

  // Reset the form
  const resetForm = () => {
    setCurrentPrompt({
      id: null,
      imageTheme: {},
      action: {},
      prompt: '',
      suggestions: []
    });
    setIsNewTheme(false);
    setShowForm(false);
    setIsLoading(false);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentPrompt.id) {
      updatePrompt();
    } else {
      createPrompt();
    }
  };

  // Edit a prompt
  const editPrompt = (prompt) => {
    setCurrentPrompt(prompt);
    setIsNewTheme(false);
    setShowForm(true);
  };

  // Handle theme selection
  const handleThemeChange = (e) => {
    const value = e.target.value;
    if (value === "new") {
      setIsNewTheme(true);
      setCurrentPrompt({...currentPrompt, imageTheme: {}});
    } else {
      const selectedTheme = imageThemes.find(theme => 
        getLocalizedValue(theme, currentLanguage) === value
      );
      setIsNewTheme(false);
      setCurrentPrompt({...currentPrompt, imageTheme: selectedTheme || {}});
    }
  };

  // Handle multilingual theme name change
  const handleThemeChange_ML = (lang, value) => {
    setCurrentPrompt({
      ...currentPrompt,
      imageTheme: {
        [lang]: value
      }
    });
  };

  // Handle multilingual action name change
  const handleActionChange_ML = (lang, value) => {
    setCurrentPrompt({
      ...currentPrompt,
      action: {
        [lang]: value
      }
    });
  };

  // Toggle theme expansion
  const toggleTheme = (theme) => {
    setExpandedThemes(prev => ({
      ...prev,
      [theme]: !prev[theme]
    }));
  };

  // Get localized value
  const getLocalizedValue = (obj, lang) => {
    if (!obj) return '';
    if (obj[lang]) return obj[lang];
    
    // Fallback to first available language
    const firstLang = Object.keys(obj)[0];
    return obj[firstLang] || '';
  };

  // Handle adding a suggestion to the current prompt
  const addSuggestion = (suggestionKey) => {
    if (!currentPrompt.suggestions.includes(suggestionKey)) {
      setCurrentPrompt({
        ...currentPrompt,
        suggestions: [...currentPrompt.suggestions, suggestionKey]
      });
    }
  };

  // Handle removing a suggestion from the current prompt
  const removeSuggestion = (suggestionKey) => {
    setCurrentPrompt({
      ...currentPrompt,
      suggestions: currentPrompt.suggestions.filter(key => key !== suggestionKey)
    });
  };

  // Group prompts by theme
  const getPromptsByTheme = () => {
    const grouped = {};
    
    prompts.forEach(prompt => {
      const themeKey = getLocalizedValue(prompt.imageTheme, currentLanguage);
      if (!grouped[themeKey]) {
        grouped[themeKey] = [];
      }
      grouped[themeKey].push(prompt);
    });
    
    return grouped;
  };

  // Initialize component
  useEffect(() => {
    fetchPromptsAndSuggestions();
  }, []);

  // Refresh prompt sorting when language changes
  useEffect(() => {
    // Only refresh when we have data
    if (prompts.length > 0) {
      const grouped = getPromptsByTheme();
      
      // Initialize expanded state for each theme
      const initialExpandedState = {};
      Object.keys(grouped).forEach(theme => {
        initialExpandedState[theme] = expandedThemes[theme] || false;
      });
      setExpandedThemes(initialExpandedState);
    }
  }, [currentLanguage]);

  const promptsByTheme = getPromptsByTheme();

  return (
    <div>
      <SuggestionPictogramManager isAdmin={true} />
      
      <div className={`${theme.card.base} bg-opacity-5 overflow-hidden p-4`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[#0C3B2E]">{t('title')}</h2>
          <div className="flex space-x-4">
            <select 
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              className="shadow border rounded px-3 py-2 text-gray-700"
            >
              {languages.map(lang => (
                <option key={lang} value={lang}>{lang.toUpperCase()}</option>
              ))}
            </select>
            <button 
              onClick={() => { resetForm(); setShowForm(!showForm); }} 
              className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
            >
              {showForm ? t('cancel') : t('addNew')}
            </button>
          </div>
        </div>
        
        {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p>{error}</p>
        </div>}
        
        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white shadow-md rounded p-6 mb-6">
            {/* Language selector */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('language.title')}
              </label>
              <select 
                value={currentLanguage}
                onChange={(e) => setCurrentLanguage(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                {languages.map(lang => (
                  <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                ))}
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="imageTheme">
                {t('imageTheme')}
              </label>
              {!isNewTheme ? (
                <div className="flex space-x-2">
                  <select 
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={getLocalizedValue(currentPrompt.imageTheme, currentLanguage)}
                    onChange={handleThemeChange}
                    required
                  >
                    <option value="">{t('selectTheme')}</option>
                    {imageThemes.map(theme => (
                      <option key={JSON.stringify(theme)} value={getLocalizedValue(theme, currentLanguage)}>
                        {getLocalizedValue(theme, currentLanguage)}
                      </option>
                    ))}
                    <option value="new">{t('addNewTheme')}</option>
                  </select>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      {t('imageTheme')} ({currentLanguage.toUpperCase()})
                    </label>
                    <div className="flex space-x-2">
                      <input 
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        type="text" 
                        placeholder={t('enterNewTheme')}
                        value={getLocalizedValue(currentPrompt.imageTheme, currentLanguage)}
                        onChange={(e) => handleThemeChange_ML(currentLanguage, e.target.value)}
                        required={currentLanguage === 'en'} // Require at least English
                      />
                      <button 
                        type="button"
                        onClick={() => setIsNewTheme(false)}
                        className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm}`}
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </div>
                  
                  {languages.filter(lang => lang !== currentLanguage).map(lang => (
                    <div className="mb-4" key={lang}>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        {t('imageTheme')} ({lang.toUpperCase()})
                      </label>
                      <input 
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        type="text" 
                        placeholder={`${t('imageTheme')} (${lang.toUpperCase()})`}
                        value={getLocalizedValue(currentPrompt.imageTheme, lang)}
                        onChange={(e) => handleThemeChange_ML(lang, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="action">
                {t('action')} ({currentLanguage.toUpperCase()})
              </label>
              <input 
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="action"
                type="text" 
                placeholder={t('action')}
                value={getLocalizedValue(currentPrompt.action, currentLanguage)}
                onChange={(e) => handleActionChange_ML(currentLanguage, e.target.value)}
                required={currentLanguage === 'en'} // Require at least English
              />
            </div>
            
            {languages.filter(lang => lang !== currentLanguage).map(lang => (
              <div className="mb-4" key={lang}>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`action-${lang}`}>
                  {t('action')} ({lang.toUpperCase()})
                </label>
                <input 
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  id={`action-${lang}`}
                  type="text" 
                  placeholder={`${t('action')} (${lang.toUpperCase()})`}
                  value={getLocalizedValue(currentPrompt.action, lang)}
                  onChange={(e) => handleActionChange_ML(lang, e.target.value)}
                />
              </div>
            ))}
            
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="promptText">
                {t('promptText')}
              </label>
              <textarea 
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
                id="promptText"
                placeholder={t('promptText')}
                value={currentPrompt.prompt}
                onChange={(e) => setCurrentPrompt({...currentPrompt, prompt: e.target.value})}
                required
              />
              <p className="text-xs text-gray-500 mt-1">The prompt is not language-specific as it's used for AI generation.</p>
            </div>
            
            {availableSuggestions.length > 0 && (
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t('suggestions.title')}
                </label>
                
                <div className="flex flex-wrap -mx-2">
                  {availableSuggestions.map(suggestion => {
                    const isSelected = currentPrompt.suggestions.includes(suggestion.key);
                    return (
                      <div key={suggestion.key} className="px-2 py-1">
                        <div 
                          className={`flex items-center px-3 py-2 rounded cursor-pointer ${
                            isSelected ? 'bg-green-100 border border-green-500' : 'bg-gray-100 border border-gray-300'
                          }`}
                          onClick={() => isSelected ? removeSuggestion(suggestion.key) : addSuggestion(suggestion.key)}
                        >
                          {suggestion.icon.startsWith('fa-') ? (
                            <i className={`fas ${suggestion.icon} mr-2 ${isSelected ? 'text-green-600' : 'text-gray-600'}`}></i>
                          ) : (
                            <img src={suggestion.icon} alt="icon" className="h-4 w-4 mr-2" />
                          )}
                          <span className={`text-sm ${isSelected ? 'text-green-800' : 'text-gray-800'}`}>
                            {getLocalizedValue(suggestion.name, currentLanguage)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-end">
              <button 
                type="submit" 
                className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
                disabled={isLoading}
              >
                {isLoading ? t('processing') : currentPrompt.id ? t('update') : t('create')}
              </button>
            </div>
          </form>
        )}
        
        {isLoading && !Object.keys(promptsByTheme).length ? (
          <div className="bg-white p-6 text-center">{t('loading')}</div>
        ) : Object.keys(promptsByTheme).length === 0 ? (
          <div className="bg-white p-6 text-center">{t('noPrompts')}</div>
        ) : (
          <div className="space-y-4">
            {Object.entries(promptsByTheme).map(([themeKey, themePrompts]) => (
              <div 
                key={themeKey} 
                className="bg-white overflow-hidden border shadow rounded-lg"
              >
                <div 
                  className="bg-gray-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleTheme(themeKey)}
                >
                  <h3 className="text-lg font-semibold text-[#0C3B2E]">{themeKey}</h3>
                  <div className="text-sm text-gray-500">
                    {themePrompts.length} {themePrompts.length === 1 ? 'prompt' : 'prompts'}
                    <span className="ml-2">
                      {expandedThemes[themeKey] ? '▼' : '►'}
                    </span>
                  </div>
                </div>
                
                {expandedThemes[themeKey] && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('action')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('prompt')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('suggestions.title')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {themePrompts.map(prompt => (
                          <tr key={prompt.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{getLocalizedValue(prompt.action, currentLanguage)}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 truncate max-w-xs">
                                {prompt.prompt.length > 100 ? `${prompt.prompt.substring(0, 100)}...` : prompt.prompt}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1">
                                {prompt.suggestions?.map(suggestionKey => {
                                  const suggestionObj = availableSuggestions.find(s => s.key === suggestionKey);
                                  if (!suggestionObj) return null;
                                  
                                  return (
                                    <div 
                                      key={suggestionKey}
                                      className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-xs"
                                      title={getLocalizedValue(suggestionObj.name, currentLanguage)}
                                    >
                                      {suggestionObj.icon.startsWith('fa-') ? (
                                        <i className={`fas ${suggestionObj.icon} mr-1 text-gray-600`}></i>
                                      ) : (
                                        <img src={suggestionObj.icon} alt="icon" className="h-3 w-3 mr-1" />
                                      )}
                                      <span className="truncate max-w-[80px]">
                                        {getLocalizedValue(suggestionObj.name, currentLanguage)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => editPrompt(prompt)}
                                className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.sm} mr-2`}
                              >
                                {t('edit')}
                              </button>
                              <button
                                onClick={() => deletePrompt(prompt.id)}
                                className={`${theme.button.base} text-white bg-red-600 hover:bg-red-700 ${theme.button.sizes.sm}`}
                              >
                                {t('delete')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptEditor;